import { useState } from 'react';
import { resolvePhotoUrl } from '@/lib/photo';

// Phase 88 — Global Avatar rendering primitive.
//
// Single source of truth for circular identity tiles across HRMS, Students,
// Chat, Modals, etc. Behaviour:
//   - Renders an <img> when `src` resolves AND the image loads successfully.
//   - Falls back to initials in a gradient tile when src is empty OR loading fails.
//   - Optional status dot (online/offline) anchored to bottom-right of the avatar.
//
// Props:
//   src      — raw URL/path/object (any field the photo helper understands)
//   name     — used for initials + alt text
//   size     — pixel size (default 36)
//   online   — true/false/null. null = no dot rendered.
//   ring     — optional Tailwind ring class (e.g. "ring-2 ring-emerald-400")
//   tone     — 'student' | 'staff' | 'admin' — controls the initials-tile gradient
//   testId   — for data-testid annotation
export function Avatar({ src, name = '', size = 36, online = null, ring = '', tone = 'staff', testId, className = '' }) {
  const [failed, setFailed] = useState(false);
  const resolved = resolvePhotoUrl(src);
  const showImg = resolved && !failed;
  const initials = (name || '?').trim().split(/\s+/).slice(0, 2).map(s => s[0] || '').join('').toUpperCase() || '?';
  const px = `${size}px`;
  const dotSize = Math.max(8, Math.round(size * 0.22));
  const toneClass = (
    tone === 'student' ? 'from-blue-500 to-indigo-600' :
    tone === 'admin' ? 'from-purple-500 to-fuchsia-600' :
    'from-emerald-500 to-teal-600'
  );

  return (
    <div className={`relative shrink-0 ${className}`} style={{ width: px, height: px }} data-testid={testId}>
      {showImg ? (
        <img
          src={resolved}
          alt={name}
          className={`w-full h-full rounded-full object-cover border border-border ${ring}`}
          onError={() => setFailed(true)}
        />
      ) : (
        <div
          className={`w-full h-full rounded-full bg-gradient-to-br ${toneClass} text-white flex items-center justify-center font-semibold ${ring}`}
          style={{ fontSize: Math.max(10, Math.round(size * 0.38)) }}
          aria-label={name}
        >
          {initials}
        </div>
      )}
      {online !== null && (
        <span
          className={`absolute bottom-0 right-0 rounded-full border-2 border-white dark:border-slate-900 ${online ? 'bg-emerald-500' : 'bg-slate-400'}`}
          style={{ width: dotSize, height: dotSize }}
          aria-label={online ? 'online' : 'offline'}
        />
      )}
    </div>
  );
}

export default Avatar;
